import { useState, useEffect, useRef } from "react";
import { message, Modal } from "antd";
import { ExclamationCircleOutlined } from "@ant-design/icons";
import { v4 as uuidv4 } from "uuid";
import dayjs from "dayjs";
import sortBy from "lodash/sortBy";
import { useTranslation } from "react-i18next";
import { useEventBus } from "../../../../lib/components/EventBusProvider";
import db from "../../../../database";
import { timeStamp } from "../../../../utils/clock";
import { generateDocumentNo } from "../../../../utils/documentSequenceUtils";
import { getWorkflowData } from "../../../../utils/getWorkflowData";
import { stock } from "../stock";
import { discardCouponService, discardGiftCard, upsertGiftCardTransaction, upsertCart, upsertEstimate } from "../../../../services";
import messages from "../../../../errors/messages.en-IN.json";
import useSessionStore from "../../../../stores/useSessionStore";
import useSafeTimeout from "../../../../hooks/useSafeTimeout";
import useTransactionStore from "../../../../stores/useTransactionStore";

const useParkedBills = ({
  cart,
  setCart,
  deleteCart,
  deleteReceipt,
  orderType,
  giftCardData,
  setGiftCardData,
  documentSequence,
  setDocumnetSequence,
  tillData,
  upsertPOSLog,
  posConfig,
  authHeaders,
  getFilteredPaymentMethods,
  setShowPayments,
  setSelectedDocumentType,
  setProductSearchInput,
  setOpen,
  searchInputRef,
  setShowPaymentMethods,
  setOrderHistoryDetails,
  setSelectedProductInCart,
  setProductStock,
  setTempCart,
  setShowCompleteSaleStock,
  setCompleteSaleStockData,
  setManagementScreenShow,
  setSelectedSaleType,
  setKeyValue,
  handleSelectedSaleTypeForProducts,
  handleLoyaltyPayment,
  hasShownCustomerModalRef,
  isExchange,
  setIsparkedBill,
  tillaccess,
  metaDataRef,
}) => {
  const { t } = useTranslation();
  const { dispatchEvent } = useEventBus();
  const { safeSetTimeout } = useSafeTimeout();

  const storedParkedList = useSessionStore.getState().parkedList;
  const initialParkedList = storedParkedList ? storedParkedList : [];
  const [displayParkedBillModal, setDisplayParkedBillModal] = useState(false);
  const [parkedList, setParkedList] = useState(initialParkedList);
  const [filterdParkedList, setFilterdParkedList] = useState(initialParkedList);
  const [parkedBillSearchInput, setParkedBillSearchInput] = useState("");

  const parkedBillInFlightRef = useRef(false);

  const parkedListRef = useRef(initialParkedList);
  useEffect(() => {
    parkedListRef.current = parkedList;
  }, [parkedList]);

  const mapParkedList = (list) => {
    return list.map((item) => ({
      ...item,
      customer: item?.parkedCart?.customer?.name || "",
      status: "Parked",
      key: item.key,
      qty: item?.parkedCart?.totalQty || 0,
    }));
  };

  const discardParkedBill = async (record) => {
    upsertPOSLog(record.parkedCart, "DPO");

    // Remove from store / main list
    const localParkedList = useSessionStore.getState().parkedList || [];
    const updatedList = localParkedList.filter((item) => item.key !== record.key);
    useSessionStore.getState().setParkedList(updatedList);
    setParkedList([...updatedList]);

    // Update filtered list based on current search input
    if (!parkedBillSearchInput) {
      setFilterdParkedList(mapParkedList(updatedList));
    } else {
      const lowercaseKey = parkedBillSearchInput.toLowerCase();
      const filteredBills = updatedList.filter((item) => {
        const lowercaseCustomer = item?.parkedCart?.customer?.name?.toLowerCase() || "";
        const lowercaseParkedDocNo = item.parkedDocNo?.toLowerCase() || "";
        return lowercaseCustomer.includes(lowercaseKey) || lowercaseParkedDocNo.includes(lowercaseKey);
      });
      setFilterdParkedList(mapParkedList(filteredBills));
    }

    // Save discarded cart to DB
    const tillSession = useSessionStore.getState().tillSession;
    const tillSessionId = tillSession?.tillSessionId;
    let cartToDb = record.parkedCart;
    cartToDb.orderTime = dayjs(new Date()).format("DD-MM-YYYY HH:mm:ss");
    cartToDb.createdBy = tillData.tillAccess?.csUserId || "";
    cartToDb.orderType = orderType?.cwrSaletype?.cwrSaletypeId || "";
    cartToDb.orderDate = dayjs(new Date()).format("DD-MM-YYYY");
    cartToDb.tillSessionId = tillSessionId;
    cartToDb.key = uuidv4().replace(/-/g, "").toUpperCase();
    cartToDb.isSynced = 0;
    cartToDb.syncAttempts = 0;
    cartToDb.customerSearchKey = cart?.customer?.code || "";
    cartToDb.total = 0;
    cartToDb.totalQty = 0;
    cartToDb.tax = 0;
    cartToDb.isReturn = false;
    cartToDb.items.forEach((item) => {
      item.salePrice = 0;
      item.sunitprice = 0;
      item.netStd = 0;
      item.nettotal = 0;
      item.unitTax = 0;
      item.taxAmount = 0;
      item.weight = 0;
      item.unitPrice = 0;
      item.netList = 0;
      item.discount = 0;
      item.isReturn = false;
      item.linetax = 0;
      item.linenet = 0;
      item.linegross = 0;
      item.linestd = 0;
      item.netunit = 0;
      item.listPrice = 0;
      item.grossunit = 0;
      item.grossstd = 0;
      item.grosslist = 0;
      item.realPrice = 0;
    });
    const cancelledSeq = useSessionStore.getState().cancelledOrderSequence;
    if (cancelledSeq) {
      cartToDb.documentno = generateDocumentNo(cancelledSeq);
      useSessionStore.getState().incrementCancelledOrderSequence();
    }
    cartToDb.paid = 0;
    cartToDb.payments = [];
    cartToDb.change = 0;
    cartToDb.discount = 0;
    cartToDb.couponInput = [];
    await db.orders.put(cartToDb);
  };

  const retrieveParkedData = async (record) => {
    const parkedCart = record?.parkedCart || {};
    const saleTypeId = parkedCart?.saleType?.cwrSaletypeId;

    try {
      const saleTypes = await db.posSaletypes.toArray();
      const saleTypeData = saleTypes.find((item) => item.cwrSaletype?.cwrSaletypeId === saleTypeId);

      if (saleTypeData?.cwrSaletype?.name) {
        setSelectedSaleType(saleTypeData.cwrSaletype.name);
      }

      if (cart.items.length > 0) {
        openDisplayParkedBillModal("retrieve", record);
      } else {
        selectParkedBill(record, "management");
      }
    } catch (err) {
      console.error("Error retrieving sale type from DB:", err);
      message.error(`${messages.info.retrieve_saletype_failed}`);
    }
  };

  const openDisplayParkedBillModal = (param, record) => {
    if (useTransactionStore.getState().selectedReturnType === "EXCHANGE") {
      message.warning("Hold is not allowed during Return & Exchange.");
      return;
    }
    if (cart.items.length > 0) {
      Modal.confirm({
        title: "Save Cart Items ?",
        icon: <ExclamationCircleOutlined />,
        content: (
          <div>
            You can retrieve the bill later by selecting the 'Retrieve' option in Parked Bills.
            <br />
            Do you want to continue parking the bill?
          </div>
        ),
        okText: "Yes",
        cancelText: "No",
        autoFocusButton: null,
        okButtonProps: { style: { display: "inline-block" } },
        async onOk() {
          if (cart?.estimateId) {
            try {
              const bunitId = tillData?.tillAccess?.csBunit?.csBunitId;
              const estimateId = cart.estimateId;
              const date = dayjs().format("YYYY-MM-DD HH:mm:ss");
              const response = await upsertEstimate({ bunitId, estimateId, date, status: "PE", isAdvanceOrder: "N", estimateJson: cart });
              if (response?.data?.data?.upsertEstimate?.status === "200") {
                message.success(response?.data?.data?.upsertEstimate?.message);
                deleteCart();
              } else {
                message.error("Something is wrong in upsertEstimate service");
              }
            } catch (err) {
              console.error("Error parking estimate:", err);
            }
            return;
          }
          if (posConfig?.saveParkedBills === "Y") {
            const csBunitId = tillData?.tillAccess?.csBunit?.csBunitId;
            const cartItems = cart;
            const cwrPosCartId = cartItems?.cwr_pos_cart_id || uuidv4().replace(/-/g, "").toUpperCase();
            const cwrTillId = tillData?.tillAccess?.cwrTill?.cwrTillID;
            const status = "DR";

            const cartPayload = {
              csBunitId,
              cwrPosCartId,
              cwrTillId,
              status,
              cartItems,
            };

            const response = await upsertCart(cartPayload);
            if (response?.status === 200) {
              if (response?.data?.data?.upsertCart?.status === "200") {
                message.success(response?.data?.data?.upsertCart?.message);
                deleteReceipt();
              } else if (response?.data?.data?.upsertCart?.status === "201") {
                message.error(response?.data?.data?.upsertCart?.message);
              } else {
                message.error("Something is wrong in upsertCart service");
              }
            } else {
              message.warning("Something is wrong in upsertCart service");
            }
          } else {
            setSelectedDocumentType("Invoice");
            let PaymentMethods = getFilteredPaymentMethods() || [];
            let payments =
              PaymentMethods.length > 5
                ? sortBy(PaymentMethods, "sequenceNo").slice(
                    0,
                    tillData?.tillAccess?.csBunit?.b2cCustomer?.cwrCustomerId === cart?.customer?.cwrCustomerId && PaymentMethods.length === 5 ? 5 : (cart.layAway === "Y" || cart?.creditSale === "Y") ? 4 : 5
                  )
                : PaymentMethods;

            setShowPayments(payments);
            parkBill(param, record);
            const productSearchInput = document.getElementById("sm-product-search");
            safeSetTimeout(() => {
              setOpen(false);
            }, 600);
            if (searchInputRef?.current) {
              searchInputRef?.current?.focus();
            }
            setProductSearchInput("");
          }
        },
        onCancel() {
          const productSearchInput = document.getElementById("sm-product-search");
          productSearchInput?.focus();
          if (searchInputRef?.current) {
            searchInputRef.current.focus();
          }
          setOpen(false);
          setProductSearchInput("");
          setDisplayParkedBillModal(true);
        },
      });
    } else {
      setProductSearchInput("");
      setDisplayParkedBillModal(true);
    }
  };

  const discardGiftCards = async (cart) => {
    let setAuthTokens;
    if (authHeaders && authHeaders.access_token) {
      setAuthTokens = authHeaders.access_token;
    }
    if (giftCardData.length > 0) {
      let details = [];
      cart?.giftCardData?.map((ele) => {
        details.push(`{
            cardNo: ${ele.number ? `"${ele.number}"` : null}
            referenceNo: "${ele.refId}"
                }`);
      });
      discardGiftCard(null, null, details);
      let upsertDetails = [];
      giftCardData.map((ele) => {
        upsertDetails.push(`{
            cardNo: ${ele.number ? `"${ele.number}"` : null}
            type: "RD"
            referenceNo: "${cart.referenceId}"
            amount: ${ele.amount * -1}
            cardPin: ${ele?.pin ? `"${ele?.pin}"` : null}
            tillId: ${tillData?.tillAccess?.cwrTill?.cwrTillID ? `"${tillData?.tillAccess?.cwrTill?.cwrTillID}"` : null}
            b2c_Customer_Id: ${cart.customer.cwrCustomerId ? `"${cart.customer.cwrCustomerId}"` : null}
                }`);
      });
      const data = {
        query: `
            mutation {
              upsertGiftCardTransaction(giftCardTransactions: [${upsertDetails}]) {
                status
                message
              }
            }`,
      };
      await upsertGiftCardTransaction(data);
    }
  };

  const processWithParkedBillCancellation = (record) => {
    let setAuthTokens;
    if (authHeaders && authHeaders.access_token) {
      setAuthTokens = authHeaders.access_token;
    }
    discardGiftCards(record.parkedCart);
    if (record?.parkedCart?.couponInput?.length > 0) {
      const serverUrl = process.env.REACT_APP_serverUrl;

      const couponsData = [...record.parkedCart.couponInput];
      // Function to discard a coupon
      const discardCoupon = async (coupon) => {
        const discardCouponQuery = {
          query: `query {
              discardCoupon(couponcode: "${coupon.couponCode}", referenceId: "${coupon.referenceId}") {
                status
                message
              }
            }`,
        };
        try {
          const response = await discardCouponService(coupon.couponCode, coupon.referenceId);
        } catch (error) {
          console.error(`Error discarding coupon ${coupon.couponCode}:`, error);
        }
      };
      couponsData.forEach((coupon) => {
        discardCoupon(coupon);
      });
    }
    let index = record.parkedCart.payments.findIndex((payment) => payment.name.toLowerCase() === "loyalty" && !record.parkedCart.isReturn);
    if (index >= 0) {
      handleLoyaltyPayment(record.parkedCart.payments[index], record.parkedCart);
    }
    setGiftCardData([]);
    discardParkedBill(record);
    setParkedBillSearchInput("");

    // Refresh the parked bills list
    const allParkedList = useSessionStore.getState().parkedList || [];
    setFilterdParkedList(mapParkedList(allParkedList));
    const productSearchInput = document.getElementById("sm-product-search");
    if (productSearchInput) {
      safeSetTimeout(() => {
        productSearchInput.focus();
      }, 300);
    }
  };

  const handleOk = async (record) => {
    if (parkedBillInFlightRef.current) return;
    parkedBillInFlightRef.current = true;
    try {
    const updatedItems = record?.parkedCart?.items?.map((item) => ({
      ...item,
      realWeight: item.weight,
    }));

    const updatedRecord = {
      ...record,
      parkedCart: {
        ...record.parkedCart,
        items: updatedItems,
      },
    };
    const { workflowRules, approvers } = await getWorkflowData();
    const cancelParkedBillRule = workflowRules.find((res) => res.eventName === "Cancel Parked Bill");
    if (cancelParkedBillRule) {
      dispatchEvent(cancelParkedBillRule.eventName, cancelParkedBillRule.ruleName, cancelParkedBillRule.ruleCondition, approvers, null, async (eventData) => {
        if (eventData?.approvalGranted) {
          await processWithParkedBillCancellation(updatedRecord);
        } else {
          Modal.error({
            title: "Approval Required",
            content: "The cancellation request was not approved.",
          });
        }
      });
    } else {
      await processWithParkedBillCancellation(updatedRecord);
    }
    } finally {
      parkedBillInFlightRef.current = false;
    }
  };

  const handleCancel = () => {
    // Handle the cancel action here
    Modal.destroyAll();
    const productSearchInput = document.getElementById("sm-product-search");
    if (productSearchInput) {
      safeSetTimeout(() => {
        productSearchInput.focus();
      }, 300);
    }
  };

  const discardMessage = (modalTitle, modalContent, record) => {
    Modal.confirm({
      title: modalTitle,
      content: modalContent,
      onOk: () => handleOk(record),
      okButtonProps: { style: { display: "inline-block" } },
      autoFocusButton: null,
      onCancel: handleCancel,
    });
  };

  const parkBill = (value, record) => {
    if (cart.parked !== "Y" && cart.layAway !== "Y") {
      const pbCounter = useSessionStore.getState().incrementParkedBillCounter();
      cart.documentno = `PB/${String(pbCounter).padStart(6, "0")}`;
      deleteReceipt("parked");
      if (value === "retrieve") {
        selectParkedBill(record, "management");
      }
    } else if (cart.layAway === "Y") {
      const pbCounter = useSessionStore.getState().incrementParkedBillCounter();
      cart.documentno = `PB/${String(pbCounter).padStart(6, "0")}`;
      deleteReceipt("parked");
    } else {
      if (value === "retrieve") {
        selectParkedBill(record, "management");
      } else {
        deleteReceipt("parked");
      }
    }
    const presentParkedList = value === "parkKey" ? parkedListRef.current : useSessionStore.getState().parkedList || [];
    cart.parked = "Y";
    const presentCart = cart;
    const presentTimeStamp = timeStamp();
    const parkedBill = {
      parkedCart: presentCart,
      parkedTime: presentTimeStamp,
      parkedDocNo: cart.documentno,
      parkedOrderID: cart.items[0].sOrderReturnId,
      parkedBillId: uuidv4().replace(/-/g, "").toUpperCase(),
      qty: presentCart.totalQty,
    };
    presentParkedList.push(parkedBill);
    useSessionStore.getState().setParkedList([...presentParkedList]);
    setParkedList([...presentParkedList]);
    hasShownCustomerModalRef.current = false;

    {
      message.success(`${t("bill_parking_successful")}`);
    }
  };

  const selectParkedBill = async (item, fieldName) => {
    const listItemIndex = parkedList.findIndex((bill) => bill.parkedBillId === item.parkedBillId);
    const selectedParkedBill = { ...parkedList[listItemIndex].parkedCart, isRetrived: "Y" };

    let stockCheckResult = true;
    let isFinalValidation = false;
    let isCompleteSale = false;
    if (navigator.onLine && tillData?.tillAccess?.csBunit?.allowNegativeStock !== "Y") {
      stockCheckResult = await stock({
        cart: selectedParkedBill,
        setCart,
        tillData,
        setProductStock,
        isFinalValidation,
        setTempCart,
        isCompleteSale: true,
        setShowCompleteSaleStock,
        setCompleteSaleStockData,
      });
      if (!stockCheckResult) {
        setSelectedProductInCart({});
        return;
      } else {
        setShowPaymentMethods(true);
        setOrderHistoryDetails([]);
        setIsparkedBill(false);
      }
    } else {
      setShowPaymentMethods(true);
      setOrderHistoryDetails([]);
      setIsparkedBill(false);
    }
    // Remove the selected bill from the parked list
    const updatedParkedList = [...(useSessionStore.getState().parkedList || [])];
    updatedParkedList.splice(listItemIndex, 1);
    setParkedList(updatedParkedList);
    useSessionStore.getState().setParkedList(updatedParkedList);

    // Handle layaway logic
    if (selectedParkedBill?.layAway === "Y") {
      setSelectedDocumentType("Advance");

      const PaymentMethods = getFilteredPaymentMethods() || [];
      const maxPayments =
        PaymentMethods.length > 5
          ? sortBy(PaymentMethods, "sequenceNo").slice(
              0,
              tillData?.tillAccess?.csBunit?.b2cCustomer?.cwrCustomerId === cart?.customer?.cwrCustomerId && PaymentMethods.length === 5 ? 5 : (cart.layAway === "Y" || cart?.creditSale === "Y") ? 4 : 5
            )
          : PaymentMethods;

      setShowPayments(maxPayments);
    }
    if (!isExchange) {
      setCart(selectedParkedBill);
    }
    setDisplayParkedBillModal(false);
    if (fieldName === "management") {
      setManagementScreenShow(false);
    }
  };

  const selectLayAwayOrder = async (item, fieldName) => {
    console.log("here", item);
    const itemWithNumberTotal = {
      ...item,
      total: parseFloat(item.total),
    };
    const customMixEntry = metaDataRef.current.find((entry) => entry.key === "Custom Mix");
    let updatedCart;
    if (!customMixEntry) {
      updatedCart = {
        ...itemWithNumberTotal,
      };
    } else {
      let customMixData = [];
      try {
        customMixData = JSON.parse(customMixEntry.value);
      } catch (error) {
        console.error("Invalid JSON in Custom Mix value", error);
        return [];
      }
      const matchedIds = new Set();
      const enrichedMixes = customMixData.map((mix) => {
        const matchedChildren = mix.children
          .map((child) => {
            const matchedItem = item.items.find((line) => line.sOrderlineID === child.sOrderlineID);
            if (matchedItem) {
              matchedIds.add(child.sOrderlineID);
            }
            return {
              ...matchedItem,
              taxAmount: matchedItem?.unittax ?? 0,
              groupedItems: true,
              actualWeight: matchedItem?.weight / mix.weight,
              baseQty: matchedItem?.weight / mix.weight,
            };
          })
          .filter(Boolean);

        return {
          ...mix,
          children: matchedChildren,
          nettotal: mix.nettotal,
          sunitprice: mix.nettotal,
          originalPrice: mix.nettotal,
          salePrice: mix.nettotal - mix.taxAmount,
          realPrice: mix.nettotal - mix.taxAmount,
          addNewLine: "N",
          batchedForSale: "N",
          batchedForStock: "N",
          batchedProduct: "N",
          batchno: null,
          bunitName: "",
          csBunitId: null,
          customAttributes: [],
          preparationTime: "",
          printerId: "",
          cwrMenuTimeslot: [],
          description: "",
          discount: mix.discount,
          discountName: "",
          foodType: "",
          itemType: "",
          grosslist: 0,
          grossstd: 0,
          grossunit: 0,
          hsncode: "",
          imageUrl: "",
          imageurl: "",
          isDecimal: false,
          isDecimalQty: false,
          isManualQty: false,
          isPromoApplicable: false,
          isQtyDesimal: mix?.uomData?.[0]?.stdprecision ?? 2,
          isReturn: false,
          key: 0,
          lineId: uuidv4().replace(/-/g, "").toUpperCase(),
          linegross: 0,
          linenet: 0,
          linetax: 0,
          listPrice: 0,
          listprice: 0,
          mBatchId: null,
          mPricingruleId: null,
          multiPrice: "N",
          name2: "",
          netList: 0,
          netStd: 0,
          netunit: 0,
          newCustomAttributes: [],
          order: "N",
          originalPrice: 0,
          priceList: [],
          primaryOrderLine: null,
          productAddons: [],
          productCategoryName: "",
          productId: uuidv4().replace(/-/g, "").toUpperCase(),
          productManufacturerId: null,
          productionCenter: "",
          productionCenterId: "",
          returnQty: null,
          salesRepId: null,
          salesRepName: "",
          selectedAddons: [],
          shelfLife: "",
          shortDescription: "",
          showAddons: "N",
          taxCategory: "",
          taxFlag: "N",
          taxName: "",
          type: "I",
          unitPrice: 0,
          unitTax: 0,
          upc: "",
          upcPricingRule: null,
          value: "",
        };
      });
      const unmatchedItems = item.items.filter((line) => !matchedIds.has(line.sOrderlineID));
      const allItems = [...enrichedMixes, ...unmatchedItems];
      updatedCart = {
        ...itemWithNumberTotal,
        items: allItems,
      };
    }
    setShowPaymentMethods(true);
    const filteredItemsForStockCheck = updatedCart.items.filter((item) => item.productSegment !== "CB" && item.type === "I");
    const stockCart = {
      ...updatedCart,
      items: filteredItemsForStockCheck,
    };
    let stockCheckResult = true;
    let isFinalValidation = false;
    if (navigator.onLine && updatedCart?.preOrder === "Y" && tillData?.tillAccess?.csBunit?.allowNegativeStock !== "Y") {
      stockCheckResult = await stock({
        cart: stockCart,
        setCart,
        tillData,
        setProductStock,
        isFinalValidation,
        setTempCart,
      });
      if (!stockCheckResult) {
        setSelectedProductInCart({});
        return;
      } else {
        setCart(updatedCart);
      }
    }
    setCart(updatedCart);
    if (tillaccess?.layout === "2") {
      setKeyValue("2");
      const saleData = await db.posSaletypes.toArray();

      const selectedSaleTypeRecord = saleData.find((item) => item.cwrSaletype.cwrSaletypeId === updatedCart.saleType?.cwrSaletypeId);
      if (selectedSaleTypeRecord) {
        handleSelectedSaleTypeForProducts(selectedSaleTypeRecord);
      }
    }
  };

  const handleParkedBillSearchInput = (e) => {
    setParkedBillSearchInput(e.target.value);
    searchParkedBill(e.target.value);
  };

  const searchParkedBill = (key) => {
    const allParkedList = useSessionStore.getState().parkedList || [];
    if (!key) {
      setFilterdParkedList(mapParkedList(allParkedList));
      return;
    }

    const lowercaseKey = key.toLowerCase();

    const filteredBills = allParkedList.filter((list) => {
      const lowercaseCustomer = list?.parkedCart?.customer?.name?.toLowerCase() || "";
      const lowercaseParkedDocNo = list.parkedDocNo?.toLowerCase() || "";
      return lowercaseCustomer.includes(lowercaseKey) || lowercaseParkedDocNo.includes(lowercaseKey);
    });
    setFilterdParkedList(mapParkedList(filteredBills));
  };

  const closeParkedBillModal = () => {
    setDisplayParkedBillModal(false);
    setParkedBillSearchInput("");
    setFilterdParkedList([...parkedList]);
  };

  return {
    // State
    displayParkedBillModal,
    setDisplayParkedBillModal,
    parkedList,
    setParkedList,
    filterdParkedList,
    setFilterdParkedList,
    parkedBillSearchInput,
    setParkedBillSearchInput,
    parkedListRef,

    // Handlers
    openDisplayParkedBillModal,
    parkBill,
    selectParkedBill,
    selectLayAwayOrder,
    discardParkedBill,
    retrieveParkedData,
    discardGiftCards,
    processWithParkedBillCancellation,
    searchParkedBill,
    handleParkedBillSearchInput,
    mapParkedList,
    closeParkedBillModal,
    discardMessage,
    handleOk,
    handleCancel,
  };
};

export default useParkedBills;
